import { useState, useMemo, useEffect } from 'react';
import { BUILD_YOUR_OWN_SOFA_LINK } from 'commons/constants';
import usePdpLastHoveredOptionsExtended from 'components/consumer/PDP/state/pdpLastHoveredOptionsExtended/usePdpLastHoveredOptionsExtended';
import usePdpLastHoveredVariantSku from 'components/consumer/PDP/state/pdpLastHoveredVariantSku';
import usePdpSelectedOptionsVariantSku from 'components/consumer/PDP/state/pdpSelectedOptionsVariantSku/usePdpSelectedOptionsVariantSku';
import usePDPOptions from 'components/consumer/PDP/state/context/usePDPOptions';
import useModularPDPState from '../useModularPDPState';
import useProductBasicDetails from '../useProductBasicDetails';
import { getAllPreconfigHeroImages, findSectionalPreconfiguredProduct } from './utils';
import { getConfigPhotosForSelectedOptions } from '../usePDPPhotos/utils';
const useModularPDPPhotos = (id = null, slug = null, hookOptions = {}) => {
  // Callers on non-modular PDPs discard this hook's result. Skip the basic
  // details suspense query so a location re-key cannot suspend the gallery
  // boundary through it (CON-8543).
  const {
    skip = false
  } = hookOptions;
  const {
    loading: modularStateLoading,
    error: modularStateError,
    data: {
      modularState = {}
    },
    actions: {
      setModularSectionalProduct,
      updateCurrentGalleryProduct
    }
  } = useModularPDPState(id, slug);
  const {
    data: optionsData,
    loading: optionLoading,
    error: optionError
  } = usePDPOptions();
  const {
    data: basicDetailsData
  } = useProductBasicDetails(id, slug, undefined, {
    skip
  });
  const [configurationPhotos, setConfigurationPhotos] = useState([]);
  const [studioPhotos, setStudioPhotos] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [lastHoveredConfigPhotos, setLastHoveredConfigPhotos] = useState([]);
  const [modularAllHeroGallery, setAllHeroGallery] = useState([]);
  const [currentGalleryProductPhotos, setCurrentGalleryProductPhotos] = useState([]);
  const selectedPhotoToShow = useMemo(() => {
    if (!selectedPhoto) {
      return null;
    }
    let photoSet = [];
    if (selectedPhoto.type === 'studio') {
      photoSet = studioPhotos;
    } else if (selectedPhoto.type === 'configuration' || selectedPhoto.type === 'cylindo') {
      photoSet = configurationPhotos;
    } else if (selectedPhoto.type === 'buildyourown') {
      return {
        id: BUILD_YOUR_OWN_SOFA_LINK,
        src: BUILD_YOUR_OWN_SOFA_LINK,
        type: 'buildyourown'
      };
    }
    return photoSet[selectedPhoto.index];
  }, [selectedPhoto, studioPhotos, configurationPhotos]);
  useEffect(() => {
    setStudioPhotos([]);
    setConfigurationPhotos([]);
    setSelectedPhoto?.(null);
  }, [modularState?.modularParentSlug]);
  useEffect(() => {
    if (!modularState?.preConfiguredProducts?.length || !optionsData?.options?.length || !basicDetailsData) {
      return;
    }
    const allHeroImgs = getAllPreconfigHeroImages({
      preConfiguredProducts: [basicDetailsData],
      options: optionsData?.options
    });
    setStudioPhotos(allHeroImgs);
    setAllHeroGallery(allHeroImgs);
    // TODO: Refactor useModularPDPState to return stable identities for
    // functions, then add all dependencies here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsData?.options, modularState?.preConfiguredProducts, modularState?.modularParentSlug]);
  useEffect(() => {
    if (!modularState?.modularParentSlug || !modularState?.preConfiguredProducts?.length) {
      return;
    }
    const sectionalProduct = findSectionalPreconfiguredProduct(modularState.preConfiguredProducts);
    if (sectionalProduct && modularState?.modularSectionalProduct?.name !== sectionalProduct.name) {
      setModularSectionalProduct(sectionalProduct);
      if (modularState?.currentModularGalleryProduct?.id !== sectionalProduct.id) {
        updateCurrentGalleryProduct(sectionalProduct);
      }
    }
    // TODO: Refactor useModularPDPState to return stable identities for
    // functions, then add all dependencies here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modularState?.modularParentSlug, modularState?.preConfiguredProducts]);
  useEffect(() => {
    setSelectedPhoto?.(prevSelectedPhoto => {
      if (modularAllHeroGallery?.length && prevSelectedPhoto?.type !== 'studio') {
        return {
          type: 'studio',
          index: 0
        };
      }
      return prevSelectedPhoto;
    });
  }, [modularAllHeroGallery]);
  const selectedOptionsVariantSku = usePdpSelectedOptionsVariantSku();
  useEffect(() => {
    if (!selectedOptionsVariantSku || !modularState?.modularSectionalProduct) {
      return;
    }
    const images = getConfigPhotosForSelectedOptions(selectedOptionsVariantSku, optionsData.selectedOptionsExtended, optionsData.upholsteryOptionId, modularState.modularSectionalProduct);
    if (optionsData?.pdpHasUserSelectedOptions && (selectedPhoto?.type !== 'configuration' || selectedPhoto?.index > images.length - 1) && images.length) {
      setSelectedPhoto?.({
        type: 'configuration',
        index: 0
      });
    }
    let notChanged = configurationPhotos.length === images.length;
    for (let i = 0; i < images.length; i++) {
      if (configurationPhotos[i]?.src !== images[i]?.src) {
        notChanged = false;
        break;
      }
    }
    if (notChanged) {
      return;
    }
    setConfigurationPhotos(images);
  }, [configurationPhotos, modularState?.modularParentSlug, modularState?.modularSectionalProduct, optionsData?.selectedOptionsExtended, selectedOptionsVariantSku, optionsData?.upholsteryOptionId, optionsData?.pdpHasUserSelectedOptions, selectedPhoto?.index, selectedPhoto?.type]);
  const pdpLastHoveredOptionsExtended = usePdpLastHoveredOptionsExtended();
  const pdpLastHoveredVariantSku = usePdpLastHoveredVariantSku();
  useEffect(() => {
    if (pdpLastHoveredVariantSku) {
      return;
    }
    const {
      upholsteryOptionId
    } = optionsData;
    const images = getConfigPhotosForSelectedOptions(pdpLastHoveredVariantSku, pdpLastHoveredOptionsExtended, upholsteryOptionId, modularState?.modularSectionalProduct);
    setLastHoveredConfigPhotos(images);
    // TODO: Adding all dependencies here appears to cause an Apollo error, we
    // should investigate this further and refactor if necessary
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdpLastHoveredVariantSku]);
  useEffect(() => {
    if (modularState?.modularTotalPrice > 0 || !modularState?.modularSectionalProduct) {
      return;
    }
    if (modularState?.modularTotalPrice === 0 && modularState?.currentModularGalleryProduct.id !== modularState?.modularSectionalProduct?.id) {
      updateCurrentGalleryProduct(modularState?.modularSectionalProduct);
    }
    // TODO: Refactor useModularPDPState to return stable identities for
    // functions, then add all dependencies here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modularState?.modularTotalPrice]);
  useEffect(() => {
    if (!modularState?.currentModularGalleryProduct) {
      return;
    }
    const images = getConfigPhotosForSelectedOptions(selectedOptionsVariantSku, optionsData.selectedOptionsExtended, optionsData.upholsteryOptionId, modularState.currentModularGalleryProduct);
    let notChanged = configurationPhotos.length === images.length;
    for (let i = 0; i < images.length; i++) {
      if (configurationPhotos[i]?.src !== images[i]?.src) {
        notChanged = false;
        break;
      }
    }
    if (notChanged) {
      return;
    }
    setCurrentGalleryProductPhotos(images);
  }, [configurationPhotos, modularState?.currentModularGalleryProduct, selectedOptionsVariantSku, optionsData?.selectedOptionsExtended, optionsData?.upholsteryOptionId]);
  return {
    loading: modularStateLoading || optionLoading,
    error: modularStateError || optionError,
    data: {
      enableCrops: true,
      studioPhotos,
      configurationPhotos,
      currentGalleryProductPhotos,
      dimensionalImage: null,
      lastHoveredConfigPhotos,
      selectedPhoto: selectedPhotoToShow,
      isConfigFlipped: false,
      introVideoIds: null,
      isVideoSelected: false,
      productName: basicDetailsData?.name
    },
    actions: {
      setSelectedPhoto,
      updateGalleryProduct: updateCurrentGalleryProduct
    }
  };
};
export default useModularPDPPhotos;